import { useState } from 'react';
import {
    Zap,
    Users,
    Target,
    DollarSign,
    UserPlus,
    Lock,
    Star,
    TrendingUp,
    CheckCircle,
    Calendar,
    AlertCircle,
    Flag
} from 'lucide-react';
const Main = () => {
    const [selectedFilter, setSelectedFilter] = useState('all');

    // Mock team data
    const teamMembers = [
        {
            id: 1,
            name: "Sarah Parker",
            target: 200000,
            current: 165000,
            leads: 8
        },
        {
            id: 2,
            name: "James Wilson",
            target: 200000,
            current: 142000,
            leads: 6
        },
        {
            id: 3,
            name: "Michael Chen",
            target: 200000,
            current: 188000,
            leads: 7
        }
    ];

    // Mock available high-value leads
    const availableLeads = [
        {
            id: 1,
            company: "Enterprise AI Solutions",
            value: 85000,
            probability: 85,
            timeframe: "2 weeks",
            status: "unassigned",
            type: "New Business",
            boostPotential: "high",
            requiredMeetings: 2,
            nextSteps: ["Technical Review", "Commercial Meeting"]
        },
        {
            id: 2,
            company: "Global Biotech Corp",
            value: 120000,
            probability: 75,
            timeframe: "1 week",
            status: "unassigned",
            type: "Portfolio Expansion",
            boostPotential: "very high",
            requiredMeetings: 3,
            nextSteps: ["Portfolio Review", "Strategy Session", "Commercial Discussion"]
        }
    ];

    // Mock boost campaigns
    const boostCampaigns = [
        {
            id: 1,
            name: "Q1 Final Push",
            target: 150000,
            progress: 95000,
            deadline: "2 weeks",
            status: "active",
            team: ["Sarah Parker", "James Wilson"],
            meetings: [
                {
                    time: "Tomorrow 10:00",
                    company: "TechVision AI",
                    value: 45000
                },
                {
                    time: "Thursday 14:00",
                    company: "DataSphere Ltd",
                    value: 35000
                }
            ]
        }
    ];
    return (
        <div className="space-y-6">
            {/* Team Overview */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                       <span className='font-bold'> Team Performance</span>
                    </div>
                </div>
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                        {teamMembers.map((member) => (
                            <div key={member.id} className="p-4 border rounded-lg">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h3 className="font-medium">{member.name}</h3>
                                        <p className="text-sm text-gray-500">{member.leads} active leads</p>
                                    </div>
                                    <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm
                                        ${(member.current / member.target) >= 0.9 ? "bg-[#d1fae5] text-[#065f46]" :
                                            (member.current / member.target) >= 0.7 ? "bg-blue-100 text-blue-800" :
                                                "outline"}
                                    `}>
                                        {((member.current / member.target) * 100).toFixed(0)}%
                                    </div>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                    <div className="bg-green h-2 rounded-full" style={{ width: `${((member.current / member.target) * 100)}%` }}></div>
                                </div>
                                <div className="text-sm text-gray-500">
                                    £{(member.current / 1000).toFixed(0)}k / £{(member.target / 1000).toFixed(0)}k
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div>

                {/* Available High-Value Leads */}
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div>
                        <div className="flex items-center gap-2">
                            <Star className="w-5 h-5" />
                            <span className='font-bold'> Available High-Value Leads</span>
                        </div>
                        <div>
                            Unassigned opportunities for immediate action
                        </div>
                    </div>
                    <div>
                        <div className="space-y-4 mt-2">
                            {availableLeads.map((lead) => (
                                <div key={lead.id} className="p-4 border rounded-lg">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h3 className="font-medium flex items-center gap-2">
                                                {lead.company}
                                                {lead.boostPotential === 'very high' && (
                                                    <div className="text-xs flex items-center font-medium me-2 px-2.5 py-0.5 rounded-sm bg-purple-100 text-purple-800">
                                                        <Zap className="w-3 h-3 mr-1" />
                                                        Boost Potential
                                                    </div>
                                                )}
                                            </h3>
                                            <p className="text-sm text-gray-500">{lead.type}</p>
                                        </div>
                                        <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                            £{(lead.value / 1000).toFixed(0)}k
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                                        <div>
                                            <span className="text-sm text-gray-500">Timeframe</span>
                                            <div className="font-medium">{lead.timeframe}</div>
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500">Probability</span>
                                            <div className="font-medium">{lead.probability}%</div>
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500">Required Meetings</span>
                                            <div className="font-medium">{lead.requiredMeetings}</div>
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500">Status</span>
                                            <div className="font-medium capitalize">{lead.status}</div>
                                        </div>
                                    </div>

                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {lead.nextSteps.map((step, index) => (
                                            <div key={index} className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                                {step}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 flex justify-end gap-2">
                                        <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green text-green">
                                            <UserPlus className="w-4 h-4" />
                                            Assign to Team
                                        </button>
                                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                            <Lock className="w-4 h-4" />
                                            Take Ownership
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>

                        {/* Active Boost Campaigns */}
                        <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                            <div>
                                <div className="flex items-center gap-2">
                                    <Zap className="w-5 h-5" />
                                    <span className='font-bold'> Active Boost Campaigns</span>
                                </div>
                                <div className="flex justify-between">
                                    <div>
                                        Accelerated revenue initiatives
                                    </div>
                                    <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                        <Zap className="w-4 h-4 mr-2" />
                                        New Boost Campaign
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="space-y-4 mt-2">
                                    {boostCampaigns.map((campaign) => (
                                        <div key={campaign.id} className="border rounded-lg">
                                            <div className="p-4 border-b">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        <h3 className="font-medium">{campaign.name}</h3>
                                                        <p className="text-sm text-gray-500">
                                                            {campaign.team.join(", ")}
                                                        </p>
                                                    </div>
                                                    <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-purple-100 text-purple-800">
                                                        Deadline: {campaign.deadline}
                                                    </div>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                                    <div className="bg-green h-2 rounded-full" style={{ width: `${((campaign.progress / campaign.target) * 100)}%` }}></div>
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    £{(campaign.progress / 1000).toFixed(0)}k / £{(campaign.target / 1000).toFixed(0)}k
                                                </div>
                                            </div>

                                            <div className="p-4">
                                                <h4 className="font-medium mb-2">Scheduled High-Value Meetings</h4>
                                                <div className="space-y-2">
                                                    {campaign.meetings.map((meeting, index) => (
                                                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                                            <div className="flex items-center gap-2">
                                                                <Calendar className="w-4 h-4 text-gray-500" />
                                                                <span>{meeting.time}</span>
                                                                <span className="text-gray-500">•</span>
                                                                <span>{meeting.company}</span>
                                                            </div>
                                                            <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">
                                                                £{(meeting.value / 1000).toFixed(0)}k
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main